import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { FormaPagamento, SalvarFormaPagamentoRequest } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SalvarFormaPagamentoRequest) => void;
  editingForma?: FormaPagamento | null;
  isLoading?: boolean;
}

const defaultValues: SalvarFormaPagamentoRequest = {
  descricao: '',
  isAtivo: true,
  habilitaCashBox: false,
  isEntrada: false,
  isRetirada: false,
  isTroca: false,
  isTransferencia: false,
  permiteReforco: false,
  permiteSangria: false,
  permiteRecebimento: false,
  permitePagamento: false,
  vendaPrazo: false,
  termoConfissaoDivida: false,
  compoemLimite: false,
  geraFatura: false,
  recebimentoAntecipado: false,
  vendaFutura: false,
  solicitaDocumento: false,
  trocoDinheiro: false,
  trocoCheque: false,
  consultaCheque: false,
  troco: false,
  geraComissao: false,
  horusPedido: false,
  horusServico: false,
  geraBoleto: false,
  gerarBoletoAutomatico: false,
  integradoTef: false,
  webTefMercadoPago: false,
};

const SwitchField = ({ control, name, label }: { control: any; name: any; label: string }) => (
  <FormField control={control} name={name}
    render={({ field }) => (
      <FormItem className="flex items-center justify-between rounded-lg border px-3 py-2">
        <FormLabel className="text-sm font-normal">{label}</FormLabel>
        <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
      </FormItem>
    )}
  />
);

export const FormaPagamentoDialog = ({ open, onOpenChange, onSubmit, editingForma, isLoading }: Props) => {
  const form = useForm<SalvarFormaPagamentoRequest>({ defaultValues });

  useEffect(() => {
    if (editingForma) {
      form.reset({
        descricao: editingForma.descricao || '',
        isAtivo: editingForma.isAtivo,
        habilitaCashBox: editingForma.habilitaCashBox,
        isEntrada: editingForma.isEntrada,
        isRetirada: editingForma.isRetirada,
        isTroca: editingForma.isTroca,
        isTransferencia: editingForma.isTransferencia,
        permiteReforco: editingForma.permiteReforco,
        permiteSangria: editingForma.permiteSangria,
        teclaAtalho: editingForma.teclaAtalho,
        permiteRecebimento: editingForma.permiteRecebimento,
        permitePagamento: editingForma.permitePagamento,
        vendaPrazo: editingForma.vendaPrazo,
        termoConfissaoDivida: editingForma.termoConfissaoDivida,
        compoemLimite: editingForma.compoemLimite,
        geraFatura: editingForma.geraFatura,
        recebimentoAntecipado: editingForma.recebimentoAntecipado,
        vendaFutura: editingForma.vendaFutura,
        solicitaDocumento: editingForma.solicitaDocumento,
        trocoDinheiro: editingForma.trocoDinheiro,
        trocoCheque: editingForma.trocoCheque,
        consultaCheque: editingForma.consultaCheque,
        troco: editingForma.troco,
        acrescimo: editingForma.acrescimo,
        desconto: editingForma.desconto,
        perBaseTrocoMaximo: editingForma.perBaseTrocoMaximo,
        geraComissao: editingForma.geraComissao,
        horusPedido: editingForma.horusPedido,
        horusServico: editingForma.horusServico,
        taxa: editingForma.taxa,
        geraBoleto: editingForma.geraBoleto,
        gerarBoletoAutomatico: editingForma.gerarBoletoAutomatico,
        integradoTef: editingForma.integradoTef,
        webTefMercadoPago: editingForma.webTefMercadoPago,
        modalidadePrazoId: editingForma.modalidadePrazoId,
        operadoraCartaoId: editingForma.operadoraCartaoId,
        idPos: editingForma.idPos,
      });
    } else {
      form.reset(defaultValues);
    }
  }, [editingForma, open, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[660px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingForma ? 'Editar Forma de Pagamento' : 'Nova Forma de Pagamento'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex gap-4">
              <FormField control={form.control} name="descricao" rules={{ required: 'Descrição é obrigatória' }}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Descrição *</FormLabel>
                    <FormControl><Input placeholder="Ex: Dinheiro, Cartão de Crédito..." {...field} /></FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="isAtivo"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center rounded-lg border px-4 py-2">
                    <FormLabel className="text-sm mb-1">Ativa</FormLabel>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )}
              />
            </div>

            <Tabs defaultValue="financeiro">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
                <TabsTrigger value="cashbox">CashBox</TabsTrigger>
                <TabsTrigger value="pedidos">Pedidos</TabsTrigger>
                <TabsTrigger value="banco">Banco/TEF</TabsTrigger>
              </TabsList>

              <TabsContent value="financeiro" className="mt-4 space-y-2">
                <SwitchField control={form.control} name="permiteRecebimento" label="Permite Recebimento" />
                <SwitchField control={form.control} name="permitePagamento" label="Permite Pagamento" />
                <SwitchField control={form.control} name="vendaPrazo" label="Venda a Prazo" />
                <SwitchField control={form.control} name="termoConfissaoDivida" label="Gera Termo de Confissão de Dívida" />
                <SwitchField control={form.control} name="compoemLimite" label="Compõe Limite de Crédito" />
                <SwitchField control={form.control} name="geraFatura" label="Gera Fatura" />
                <SwitchField control={form.control} name="recebimentoAntecipado" label="Recebimento Antecipado" />
                <SwitchField control={form.control} name="vendaFutura" label="Venda Futura" />
                <SwitchField control={form.control} name="solicitaDocumento" label="Solicita Documento" />
                <SwitchField control={form.control} name="troco" label="Permite Troco" />
                <SwitchField control={form.control} name="trocoDinheiro" label="Troco em Dinheiro" />
                <SwitchField control={form.control} name="trocoCheque" label="Troco em Cheque" />
                <SwitchField control={form.control} name="consultaCheque" label="Consulta Cheque" />
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <FormField control={form.control} name="acrescimo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Acréscimo (%)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField control={form.control} name="desconto"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Desconto (%)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField control={form.control} name="perBaseTrocoMaximo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">% Troco Máx.</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="cashbox" className="mt-4 space-y-2">
                <SwitchField control={form.control} name="habilitaCashBox" label="Habilita no CashBox" />
                <SwitchField control={form.control} name="isEntrada" label="Entrada" />
                <SwitchField control={form.control} name="isRetirada" label="Retirada" />
                <SwitchField control={form.control} name="isTroca" label="Troca" />
                <SwitchField control={form.control} name="isTransferencia" label="Transferência" />
                <SwitchField control={form.control} name="permiteReforco" label="Permite Reforço" />
                <SwitchField control={form.control} name="permiteSangria" label="Permite Sangria" />
                <FormField control={form.control} name="teclaAtalho"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tecla Atalho</FormLabel>
                      <FormControl>
                        <Input placeholder="F1-F12" {...field} onChange={e => field.onChange(e.target.value || undefined)} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="pedidos" className="mt-4 space-y-2">
                <SwitchField control={form.control} name="geraComissao" label="Gera Comissão" />
                <SwitchField control={form.control} name="horusPedido" label="Horus Pedido" />
                <SwitchField control={form.control} name="horusServico" label="Horus Serviço" />
                <FormField control={form.control} name="taxa"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Taxa (%)</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="banco" className="mt-4 space-y-2">
                <SwitchField control={form.control} name="geraBoleto" label="Gera Boleto" />
                <SwitchField control={form.control} name="gerarBoletoAutomatico" label="Gerar Boleto Automático" />
                <SwitchField control={form.control} name="integradoTef" label="Integrado TEF" />
                <SwitchField control={form.control} name="webTefMercadoPago" label="WebTEF Mercado Pago" />
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <FormField control={form.control} name="operadoraCartaoId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">ID Operadora Cartão</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField control={form.control} name="idPos"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">ID POS</FormLabel>
                        <FormControl>
                          <Input {...field} onChange={e => field.onChange(e.target.value || undefined)} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvando...' : editingForma ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
